import {createEffect, createMemo, createSignal, For, on, Show} from 'solid-js';
import {createStore, reconcile} from 'solid-js/store';
import {I18nTsx} from '@helpers/solid/i18n';
import {Message, MessageMedia, TodoCompletion} from '@layer'
import wrapRichText from '@lib/richTextProcessor/wrapRichText';

import styles from '@components/chat/bubbles/checklist.module.scss';
import CheckboxFieldTsx from '@components/checkboxFieldTsx';
import Modes from '@config/modes';
import cancelEvent from '@helpers/dom/cancelEvent';
import rootScope from '@lib/rootScope';
import Chat from '@components/chat/chat';
import {AvatarNewTsx} from '@components/avatarNew';
import classNames from '@helpers/string/classNames';
import {PeerTitleTsx} from '@components/peerTitleTsx';
import {IconTsx} from '@components/iconTsx';
import {wrapEmojiTextWithEntities} from '@lib/richTextProcessor/wrapEmojiText';
import showPremiumPopup from '@components/popups/premium';
import {toastNew} from '@components/toast';
import wrapPeerTitle from '@components/wrappers/peerTitle';
import {ConfettiContainer, ConfettiRef} from '@components/confetti';
import getPeerId from '@appManagers/utils/peers/getPeerId';
import buttonKeyDown from '@helpers/solid/buttonKeyDown';

export function ChecklistBubble(props: {
  out: boolean
  message: Message.message & {media: MessageMedia.messageMediaToDo}
  chat: Chat
  richTextOptions: Parameters<typeof wrapRichText>[1]
}) {
  const [checklist, setChecklist] = createStore(props.message.media);

  createEffect(on(() => props.message.media, (value) => {
    setChecklist(reconcile(value));
  }))

  let confetti!: ConfettiRef;

  const completionsById = createMemo(() => {
    const results: Record<number, TodoCompletion> = {};
    for(const item of checklist.completions ?? []) {
      results[item.id] = item;
    }
    return results;
  })

  const isFullyCompleted = createMemo(() => {
    return checklist.todo.list.every((item) => completionsById()[item.id]);
  })

  createEffect(on(isFullyCompleted, (value, prev) => {
    if(value && !prev) {
      confetti.create({
        mode: 'poppers',
        size: 4,
        speedScale: 0.6,
        count: 50
      });
    }
  }, {defer: true}))

  const isGroupChecklist = checklist.todo.pFlags.others_can_complete
  const isReadonly = props.message.fwd_from || !props.out && !isGroupChecklist;

  async function handleClick(id: number) {
    if(props.message.fwd_from) {
      toastNew({
        langPackKey: 'ChecklistReadonlyForwarded'
      })
      return
    }

    if(isReadonly) {
      toastNew({
        langPackKey: 'ChecklistReadonlyPersonal',
        langPackArguments: [
          await wrapPeerTitle({peerId: props.message.fromId})
        ]
      })
      return;
    }

    if(!rootScope.premium) {
      showPremiumPopup();
      return;
    }

    const wasCompleted = completionsById()[id];

    rootScope.managers.appMessagesManager.updateTodo({
      peerId: props.chat.peerId,
      mid: props.message.mid,
      taskId: id,
      action: wasCompleted ? 'uncomplete' : 'complete'
    })
  }

  return (
    <div class={styles.wrap}>
      <ConfettiContainer ref={confetti} class={styles.confetti} />
      <div class={styles.title}>
        {wrapEmojiTextWithEntities(checklist.todo.title)}
      </div>
      <I18nTsx
        key={isGroupChecklist ? 'GroupChecklist' : 'Checklist'}
        class={styles.subtitle}
      />
      <div class={styles.list}>
        <For each={checklist.todo.list}>
          {(item) => {
            const [completedById, setCompletedById] = createSignal<PeerId>(0);
            createEffect(() => {
              const completion = completionsById()[item.id];
              if(completion) {
                setCompletedById(getPeerId(completion.completed_by));
              }
            })

            return (
              <div
                class={classNames(
                  styles.item,
                  isReadonly && styles.itemReadonly,
                  completionsById()[item.id] && styles.itemCompleted
                )}
                role="checkbox"
                aria-checked={Boolean(completionsById()[item.id])}
                aria-label={item.title.text}
                aria-readonly={isReadonly ? 'true' : undefined}
                tabindex={isReadonly || !Modes.a11y ? undefined : 0}
                onClick={(evt) => {
                  cancelEvent(evt);
                  handleClick(item.id);
                }}
                onKeyDown={isReadonly ? undefined : buttonKeyDown}
                data-checklist-item-id={item.id}
              >
                {isReadonly ? (
                  <div class={styles.itemIcon}>
                    {completionsById()[item.id] ? (
                      <IconTsx icon='check1'/>
                    ) : (
                      <div class={styles.itemIconDot}/>
                    )}
                  </div>
                ) : (
                  <div class={styles.itemCheckbox}>
                    {/* the row is the checkbox only with the keyboard layer; without it the native one is back */}
                    {Modes.a11y ? (
                      <div
                        class={classNames(
                          'checkbox-field',
                          'checkbox-without-caption',
                          completionsById()[item.id] && styles.checkboxChecked
                        )}
                        aria-hidden="true"
                      >
                        <div class="checkbox-box">
                          <div class="checkbox-box-border" />
                          <div class="checkbox-box-background" />
                          <svg class="checkbox-box-check" viewBox="0 0 24 24">
                            <use href="#check" x="-1" />
                          </svg>
                        </div>
                      </div>
                    ) : (
                      <CheckboxFieldTsx
                        checked={Boolean(completionsById()[item.id])}
                      />
                    )}
                    <AvatarNewTsx
                      peerId={completedById()}
                      size={22}
                    />
                  </div>
                )}
                <div class={styles.itemTitle}>
                  <span class={styles.itemTitleText}>{wrapEmojiTextWithEntities(item.title)}</span>
                  <Show when={isGroupChecklist && completionsById()[item.id]} keyed>
                    {(it) => (
                      <PeerTitleTsx
                        class={styles.itemCompletedBy}
                        peerId={getPeerId(it.completed_by)}
                        onlyFirstName
                      />
                    )}
                  </Show>
                </div>
              </div>
            );
          }}
        </For>
      </div>
      <div class={styles.completed}>
        <I18nTsx
          key="ChecklistCompleted"
          args={[String(checklist.completions?.length ?? 0), String(checklist.todo.list.length)]}
        />
      </div>
    </div>
  )
}
